import { Banner } from '@astryxdesign/core/Banner';
import { Button } from '@astryxdesign/core/Button';
import { Card } from '@astryxdesign/core/Card';
import { HStack } from '@astryxdesign/core/HStack';
import { Text } from '@astryxdesign/core/Text';
import { VStack } from '@astryxdesign/core/VStack';
import { BellRinging, CalendarPlus } from '@phosphor-icons/react';
import { useState } from 'react';
import { useSavePushSubscription } from '@/api/public';
import type { ClientBooking, Studio } from '@/api/schemas';
import { errorText } from '@/lib/errors';
import { fmtDateTime } from '@/lib/format';
import { downloadIcs } from '@/lib/ics';
import { bookingLink } from '@/lib/myBookings';
import { pushState, subscribeToPush } from '@/lib/pwa';

/**
 * Reminder the day before. Push is offered only where it can really work;
 * otherwise the calendar file is the honest alternative. The UI states what
 * will and will not happen (preview studio, iPhone without install, …).
 */
export function ReminderCard({ studio, booking, token }: { studio: Studio; booking: ClientBooking; token: string }) {
  const save = useSavePushSubscription(studio.slug, booking.id, token);
  const [localError, setLocalError] = useState<string | null>(null);
  const state = pushState();
  const active = booking.status === 'confirmed';

  const enable = async () => {
    setLocalError(null);
    try {
      save.mutate(await subscribeToPush(studio.slug));
    } catch (err) {
      setLocalError(
        (err as Error).message === 'permission_denied'
          ? 'Вы запретили уведомления. Их можно разрешить в настройках браузера.'
          : 'Не удалось включить уведомления на этом устройстве. Добавьте запись в календарь.',
      );
    }
  };

  const ics = () =>
    downloadIcs({
      bookingId: booking.id,
      version: booking.version,
      code: booking.code,
      studioName: studio.settings.name,
      serviceName: booking.serviceName,
      startsAt: booking.startsAt,
      endsAt: booking.endsAt,
      address: studio.settings.address,
      phone: studio.settings.phone,
      url: bookingLink(studio.slug, booking.id, token),
      cancelled: booking.status === 'cancelled',
      reminderHours: studio.settings.reminderHoursBefore,
    });

  const reminder = booking.reminder;
  let pushLine: string;
  if (booking.pushSubscribed && reminder?.status === 'pending') pushLine = `Напомним ${fmtDateTime(reminder.runAt, studio.timezone)}.`;
  else if (booking.pushSubscribed && reminder?.status === 'skipped') pushLine = 'Это образец студии: уведомление запланировано, но не будет отправлено.';
  else if (booking.pushSubscribed && reminder?.status === 'sent') pushLine = 'Напоминание отправлено.';
  else if (booking.pushSubscribed) pushLine = 'Уведомления включены. До визита меньше суток — отдельного напоминания не будет.';
  else pushLine = `Пришлём уведомление за ${studio.settings.reminderHoursBefore} ч до визита.`;

  return (
    <Card>
      <VStack gap={3}>
        <HStack gap={2} vAlign="center">
          <BellRinging size={22} className="text-accent" aria-hidden />
          <Text weight="semibold">Напоминание за сутки</Text>
        </HStack>

        {active && state.kind === 'available' && (
          <>
            <Text color="secondary">{pushLine}</Text>
            {!booking.pushSubscribed && <Button label="Включить уведомление" variant="primary" clickAction={enable} isLoading={save.isPending} />}
          </>
        )}
        {active && state.kind === 'ios-install-required' && (
          <Text color="secondary">
            На iPhone уведомления приходят только из установленного приложения: «Поделиться» → «На экран „Домой“», затем откройте запись оттуда.
            Пока можно добавить запись в календарь.
          </Text>
        )}
        {active && state.kind === 'denied' && <Text color="secondary">Уведомления запрещены в настройках браузера. Добавьте запись в календарь.</Text>}
        {active && state.kind === 'unsupported' && <Text color="secondary">{state.reason} Добавьте запись в календарь.</Text>}
        {(localError || save.error) && <Banner status="warning" title={localError ?? errorText(save.error)} collapsible={false} />}

        <Button label="Добавить в календарь" icon={<CalendarPlus size={18} />} onClick={ics} />
        <Text type="supporting">Файл календаря — снимок записи: если время изменится, скачайте его заново.</Text>
      </VStack>
    </Card>
  );
}
